<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { router } from "@inertiajs/vue3"
import { useDebounceFn } from "@vueuse/core"
import { DownloadIcon } from "lucide-vue-next"
import { watch } from "vue"

import Heading from "@/components/Heading.vue"
import Badge from "@/components/custom/Badge.vue"
import InputDebounced from "@/components/custom/InputDebounced.vue"
import Pagination from "@/components/custom/Pagination.vue"
import Table from "@/components/custom/Table.vue"
import TeleworkApproveDialog from "@/components/manager/TeleworkApproveDialog.vue"
import { Button } from "@/components/ui/button"
import { InputGroup, InputGroupAddon } from "@/components/ui/input-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import { download } from "@/lib/utils"
import { formatPrice } from "@/lib/utils"
import { exportManagerTeleworksPath } from "@/routes"
import { managerTeleworksPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type {
  ManagerTelework,
  ManagerTeleworksIndex,
} from "@/types/serializers"
import type { Column } from "@/types/table"

const props = defineProps<ManagerTeleworksIndex>()

const formFilters = useForm<{
  search?: string
  status?: string
  item_type?: string
  sort_by?: string
  order?: string
}>({
  search: props.query.search || undefined,
  status: props.query.status || undefined,
  item_type: props.query.item_type || undefined,
  sort_by: props.query.sort_by || undefined,
  order: props.query.order || undefined,
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Ayuda de Teletrabajo",
    href: managerTeleworksPath(),
  },
]

const statusItems = [
  { value: null, label: "Todos los estados" },
  { value: "pending", label: "EN REVISIÓN" },
  { value: "approved", label: "APROBADO" },
  { value: "rejected", label: "RECHAZADO" },
]

const itemTypeItems = [
  { value: null, label: "Todos los tipos" },
  { value: "chair", label: "Silla Ergonomica" },
  { value: "monitor", label: "Monitor" },
]

const columns: Array<Column<ManagerTelework>> = [
  {
    key: "id",
    header: "ID",
    sortable: true,
  },
  {
    key: "user_full_name",
    header: "Empleado",
    sortable: true,
  },
  {
    key: "item_type",
    header: "Item",
    sortable: true,
  },
  {
    key: "item_name",
    header: "Marca",
    sortable: true,
  },
  {
    key: "status",
    header: "Estado",
    sortable: true,
  },
  {
    key: "price",
    header: "Importe",
    sortable: true,
    right: true,
  },
  {
    key: "updated_at",
    header: "Updated at",
    right: true,
    sortable: true,
  },
  {
    key: "purchase_date",
    header: "Fecha de compra",
    right: true,
    sortable: true,
  },
  {
    id: "action",
    key: "id",
    right: true,
    header: "Acción",
    thClass: "w-fit",
    tdClass: "w-fit",
  },
]

async function exportTeleworks() {
  const XCSRFToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  if (!XCSRFToken) {
    console.error("CSRF token not found")
    return
  }
  try {
    const response = await fetch(exportManagerTeleworksPath(), {
      method: "POST",
      headers: { "X-CSRF-Token": XCSRFToken },
    })

    if (!response.ok) throw new Error("Export failed")

    const blob = await response.blob()
    console.log(response.headers.get("Content-Disposition"))
    const filename =
      response.headers
        .get("Content-Disposition")
        ?.match(/filename="(.+)"/)?.[1] || `teleworks-${Date.now()}.xlsx`
    download(blob, filename)
  } catch (error) {
    console.error("Export error:", error)
  }
}

function refreshData() {
  router.get(
    managerTeleworksPath(),
    { ...props.query, ...formFilters.data(), page: undefined },
    { preserveState: true, preserveScroll: true },
  )
}

const debouncedRefreshData = useDebounceFn(refreshData, 100)

watch(formFilters, debouncedRefreshData)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        title="Ayuda de Teletrabajo"
        description="Gestiona reembolsos de equipamiento"
      />

      <div class="mt-4 flex items-center gap-2">
        <InputGroup class="w-88">
          <InputDebounced
            :debounce="400"
            group
            placeholder="Buscar por nombre, equipo o departamento..."
            v-model="formFilters.search"
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>

        <Select v-model="formFilters.item_type">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in itemTypeItems"
              :key="item.label"
              :value="item.value"
            >
              {{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select v-model="formFilters.status">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in statusItems"
              :key="item.label"
              :value="item.value"
            >
              {{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          class="ml-auto"
          :disabled="!records.length"
          @click="exportTeleworks"
        >
          <DownloadIcon />
          Export .xlsx
        </Button>
      </div>
    </div>

    <Table
      :data="records"
      :columns="columns"
      v-model:sort-by="formFilters.sort_by"
      v-model:order="formFilters.order"
    >
      <template #user_full_name-cell="{ row }">
        {{ row.user.full_name }}
      </template>

      <template #status-cell="{ row }">
        <Badge class="w-fit" :title="row.reject_reason" :status="row.status" />
      </template>

      <template #price-cell="{ row }">
        {{ formatPrice(row.price) }}
      </template>

      <template #purchase_date-cell="{ row }">
        {{ dateFormatter(row.purchase_date, {}, "date") }}
      </template>

      <template #updated_at-cell="{ row }">
        {{ dateFormatter(row.updated_at, {}, "dateTime") }}
      </template>

      <template #action-cell="{ row }">
        <TeleworkApproveDialog :data="row" />
      </template>

      <template #empty>No hay solicitudes</template>
    </Table>

    <Pagination class="my-4 mt-auto px-6" />
  </AppLayout>
</template>
